import { useState, useEffect } from 'react'
import { ChevronLeftIcon } from '@heroicons/react/24/outline'
import { useNavigate, useSearchParams, Link } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'
import { useToast } from '../components/Toast'

export function Auth() {
  const [searchParams, setSearchParams] = useSearchParams()
  const mode = searchParams.get('mode') || 'login'
  const redirectTo = searchParams.get('redirect') || '/dashboard'

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [displayName, setDisplayName] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [signupDone, setSignupDone] = useState(false)

  const { signIn, signUp, user } = useAuth()
  const navigate = useNavigate()
  const toast = useToast()

  useEffect(() => {
    if (user) navigate(redirectTo, { replace: true })
  }, [user, navigate])

  const isLogin = mode === 'login'

  const setMode = (m) => {
    setSearchParams(redirectTo !== '/dashboard' ? { mode: m, redirect: redirectTo } : { mode: m })
    setError(null)
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError(null)
    setLoading(true)

    try {
      if (isLogin) {
        await signIn(email, password)
        navigate(redirectTo, { replace: true })
      } else {
        const emailRedirectTo = redirectTo !== '/dashboard'
          ? `${window.location.origin}${redirectTo}`
          : undefined
        await signUp(email, password, displayName, emailRedirectTo)
        setSignupDone(true)
        toast.success('Account created! Check your email to confirm.')
      }
    } catch (err) {
      setError(err.message || 'Something went wrong. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  if (signupDone) {
    return (
      <div className="min-h-[100dvh] flex flex-col items-center justify-center px-6 bg-stone-50 dark:bg-stone-900">
        <div className="max-w-sm w-full text-center space-y-5">
          <div className="text-6xl">📬</div>
          <h2 className="text-2xl font-bold text-stone-900 dark:text-stone-100">
            Check your email
          </h2>
          <p className="text-stone-500 dark:text-stone-400">
            We sent a confirmation link to <strong>{email}</strong>. Click it to activate your account, then come back to sign in.
          </p>
          <button
            onClick={() => { setSignupDone(false); setMode('login') }}
            className="btn-primary w-full"
            type="button"
          >
            Back to Sign In
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-[100dvh] flex flex-col bg-stone-50 dark:bg-stone-900">
      {/* Header */}
      <div className="flex items-center px-4 pt-safe pt-4">
        <Link
          to="/"
          className="btn-ghost"
          aria-label="Back to home"
        >
          <ChevronLeftIcon className="w-5 h-5" />
        </Link>
      </div>

      <div className="flex-1 flex flex-col items-center justify-center px-6 py-8">
        <div className="max-w-sm w-full">
          {/* Logo */}
          <div className="flex items-center gap-3 mb-8">
            <div className="w-12 h-12 rounded-2xl bg-amber-500 flex items-center justify-center shadow-lg">
              <span className="text-2xl">🔆</span>
            </div>
            <div>
              <h1 className="text-2xl font-bold text-stone-900 dark:text-stone-100">The Beacon</h1>
              <p className="text-xs text-stone-400">jointhebeacon.com</p>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex bg-stone-100 dark:bg-stone-800 rounded-2xl p-1 mb-6">
            <button
              onClick={() => setMode('login')}
              className={`flex-1 py-2.5 rounded-xl text-sm font-semibold transition-all duration-200 ${
                isLogin
                  ? 'bg-white dark:bg-stone-700 text-stone-900 dark:text-stone-100 shadow-sm'
                  : 'text-stone-500 dark:text-stone-400'
              }`}
              type="button"
            >
              Sign In
            </button>
            <button
              onClick={() => setMode('signup')}
              className={`flex-1 py-2.5 rounded-xl text-sm font-semibold transition-all duration-200 ${
                !isLogin
                  ? 'bg-white dark:bg-stone-700 text-stone-900 dark:text-stone-100 shadow-sm'
                  : 'text-stone-500 dark:text-stone-400'
              }`}
              type="button"
            >
              Create Account
            </button>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            {!isLogin && (
              <div>
                <label htmlFor="displayName" className="label">
                  Your name
                </label>
                <input
                  id="displayName"
                  type="text"
                  className="input"
                  placeholder="Alex Johnson"
                  value={displayName}
                  onChange={e => setDisplayName(e.target.value)}
                  autoComplete="name"
                />
              </div>
            )}

            <div>
              <label htmlFor="email" className="label">
                Email
              </label>
              <input
                id="email"
                type="email"
                className="input"
                placeholder="you@example.com"
                value={email}
                onChange={e => setEmail(e.target.value)}
                required
                autoComplete="email"
                autoCapitalize="none"
              />
            </div>

            <div>
              <label htmlFor="password" className="label">
                Password
              </label>
              <input
                id="password"
                type="password"
                className="input"
                placeholder={isLogin ? '••••••••' : 'At least 8 characters'}
                value={password}
                onChange={e => setPassword(e.target.value)}
                required
                minLength={isLogin ? 1 : 8}
                autoComplete={isLogin ? 'current-password' : 'new-password'}
              />
            </div>

            {/* Error */}
            {error && (
              <div className="p-3 bg-red-50 dark:bg-red-900/20 rounded-xl border border-red-200 dark:border-red-800">
                <p className="text-sm text-red-700 dark:text-red-400">{error}</p>
              </div>
            )}

            <button
              type="submit"
              className="btn-primary w-full text-base"
              disabled={loading}
            >
              {loading ? (
                <span className="flex items-center gap-2 justify-center">
                  <svg className="w-4 h-4 animate-spin" viewBox="0 0 24 24" fill="none">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  {isLogin ? 'Signing in...' : 'Creating account...'}
                </span>
              ) : (
                isLogin ? 'Sign In' : 'Create Account'
              )}
            </button>
          </form>

          {/* Footer */}
          {isLogin && (
            <p className="text-center text-sm text-stone-400 mt-6">
              Don't have an account?{' '}
              <button
                onClick={() => setMode('signup')}
                className="text-amber-500 font-semibold"
                type="button"
              >
                Sign up
              </button>
            </p>
          )}
        </div>
      </div>
    </div>
  )
}

export default Auth
